import { useState, useEffect } from 'react';
import styles from './RailwayNavigation.module.css';

interface SectionItem {
  id: string;
  label: string;
}

const SECTIONS: SectionItem[] = [
  { id: 'about', label: 'CHI_SIAMO' },
  { id: 'services', label: 'SERVIZI' },
  { id: 'segments', label: 'SEGMENTI' },
  { id: 'tech-stack', label: 'TECH_STACK' },
  { id: 'team', label: 'TEAM' },
  { id: 'contact', label: 'CONTATTI' },
];

export const RailwayNavigation = () => {
  const [activeSection, setActiveSection] = useState('about');
  const [showSidebar, setShowSidebar] = useState(false);

  // 1. Handle appearance/disappearance symmetrically via scroll position
  useEffect(() => {
    const handleScroll = () => {
      // Fade in when scrolled past 40% of the viewport (exiting the Hero)
      if (window.scrollY > window.innerHeight * 0.4) {
        setShowSidebar(true);
      } else {
        setShowSidebar(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check once on mount

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // 2. Track active section using highest-intersection-ratio observer
  useEffect(() => {
    const visibleSections = new Map<string, number>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id;
          if (entry.isIntersecting) {
            visibleSections.set(id, entry.intersectionRatio);
          } else {
            visibleSections.delete(id);
          }
        });

        // Determine which section currently occupies the largest part of the screen
        if (visibleSections.size > 0) {
          let best: string | null = null;
          let bestRatio = -1;
          visibleSections.forEach((ratio, sectionId) => {
            if (ratio > bestRatio) {
              bestRatio = ratio;
              best = sectionId;
            }
          });
          if (best) {
            setActiveSection(best);
          }
        }
      },
      {
        rootMargin: '-100px 0px -40% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    SECTIONS.forEach((sec) => {
      const el = document.getElementById(sec.id);
      if (el) observer.observe(el);
    });

    return () => {
      SECTIONS.forEach((sec) => {
        const el = document.getElementById(sec.id);
        if (el) observer.unobserve(el);
      });
    };
  }, []);

  const activeIndex = SECTIONS.findIndex((s) => s.id === activeSection);
  const activePercentage = activeIndex >= 0 ? (activeIndex / (SECTIONS.length - 1)) * 100 : 0;

  const handleScrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav 
      className={`${styles.sidebar} ${showSidebar ? styles.visible : ''}`} 
      aria-label="Railway Page Navigation"
    >
      <div className={styles.trackWrapper}>
        {/* Underlay: Faint Railway Track */}
        <div className={styles.trackLine} />

        {/* Overlay: Active Glowing Railway Track */}
        <div className={styles.trackLineActive} style={{ height: `${activePercentage}%` }} />

        {/* Animated Telemetry Data Flowing Down to the Active Station */}
        <div className={styles.dataFlow} style={{ height: `${activePercentage}%` }} />

        {/* The Telemetry Train Pulse */}
        <div className={styles.train} style={{ top: `${activePercentage}%` }} />

        {/* Stations Container */}
        <div className={styles.stations}>
          {SECTIONS.map((sec, idx) => {
            const isActive = sec.id === activeSection;
            const isVisited = idx < activeIndex;

            return (
              <div 
                key={sec.id} 
                className={`${styles.stationWrapper} ${isActive ? styles.stationActive : ''} ${isVisited ? styles.stationVisited : ''}`}
                style={{ top: `${(idx / (SECTIONS.length - 1)) * 100}%` }}
              >
                {/* Clickable Node */}
                <button
                  onClick={() => handleScrollTo(sec.id)}
                  className={styles.nodeButton}
                  title={`Scroll to ${sec.id}`}
                  aria-label={`Scroll to ${sec.id}`}
                >
                  <span className={styles.nodePulse} />
                  <span className={styles.nodeCore} />
                </button>

                {/* Sliding Label (Visible on hover or active) */}
                <span className={styles.label}>{sec.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </nav>
  );
};
